
  import { defineComponent, PropType } from 'vue';
  import { mapGetters } from 'vuex';
  import moment from 'moment';

  import { getRuDateTime } from '@/common/utils/dates';
  import { getSecToMs } from '@/common/utils/times';

  import calculationApi from '../../api/Calculation';
  import { CALCULATION_COMMON_GETTERS_NAME } from '../../store/calculationCommon/name';
  import {
    TCalculationActiveInfo,
    TCalculationInfoParams,
    TCalculationLog,
    UploadingDataStatusTypes,
  } from '../../types/calculation-common';

  export default defineComponent({
    name: 'CalculationStatesLog',
    props: {
      type: {
        type: Number as PropType<UploadingDataStatusTypes>,
        required: true,
      },
      isUploadingFile: {
        type: Boolean,
        default: false,
      },
    },
    data(): {
      isInitLoadedData: boolean;
      intervalId?: number;
      logs: TCalculationLog[];
      lastProcessingUploadingDataStatus?: TCalculationActiveInfo;
      isFetching: boolean;
      hasPendingFetch: boolean;
      initAbortController?: AbortController;
    } {
      return {
        isInitLoadedData: true,
        intervalId: undefined,
        logs: [],
        lastProcessingUploadingDataStatus: undefined,
        isFetching: false,
        hasPendingFetch: false,
        initAbortController: undefined,
      };
    },
    created() {
      this.initLoadData();
    },
    beforeUnmount() {
      clearInterval(this.intervalId);
    },
    computed: {
      ...mapGetters({
        uploadingDataStatus: CALCULATION_COMMON_GETTERS_NAME.getUploadingDataStatus,
      }),
      getUploadingDataStatus() {
        return this.uploadingDataStatus as TCalculationActiveInfo;
      },
    },
    methods: {
      getText(log: TCalculationLog) {
        const localMoment = moment.utc(log.date).local();
        return `${getRuDateTime(localMoment)} ${log.text}`;
      },
      async initLoadData() {
        await this.fetchCalculationInfo();

        this.startLogsInterval(this.lastProcessingUploadingDataStatus);

        await this.fetchData();

        this.scrollToTheLastLogs();
      },
      async fetchCalculationInfo() {
        const params: TCalculationInfoParams = {
          pageNumber: '1',
          pageSize: '1',
          loaderType: this.type,
        };
        this.lastProcessingUploadingDataStatus = (
          await calculationApi.getCalculationInfo(params, this.initAbortController)
        ).data.items[0];
      },
      async fetchData() {
        if (!this.lastProcessingUploadingDataStatus?.processingId) return;

        if (this.isFetching) {
          this.hasPendingFetch = true;
          return;
        }

        this.isFetching = true;
        this.hasPendingFetch = false;

        try {
          const afterId = this.logs.length > 0 ? this.logs[this.logs.length - 1].id : undefined;
          const logs = (await calculationApi.getLogs(this.lastProcessingUploadingDataStatus.processingId, afterId))
            .data;

          if (this.isInitLoadedData) {
            this.logs = [...this.logs, ...logs];
          } else {
            this.logs = logs;
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.isInitLoadedData = true;
          this.isFetching = false;

          if (this.hasPendingFetch) {
            this.fetchData();
          }
        }
      },
      async scrollToTheLastLogs() {
        await this.$nextTick();

        const logContent = this.$refs.logContent as HTMLDivElement | null;

        if (!logContent) return;

        logContent.scrollTop = logContent.scrollHeight;
      },
      isAtBottom(): boolean {
        const logContent = this.$refs.logContent as HTMLDivElement | null;
        if (!logContent) return false;

        const { scrollTop, scrollHeight, clientHeight } = logContent;

        return scrollHeight - scrollTop - clientHeight <= 5;
      },
      startLogsInterval(activeInfo?: TCalculationActiveInfo) {
        if (activeInfo?.isProgress && this.type === activeInfo?.type) {
          this.isInitLoadedData = false;
          this.intervalId = setInterval(this.fetchData, getSecToMs(5));
        }
      },
    },
    watch: {
      logs(newData: TCalculationLog[], oldData: TCalculationLog[]) {
        if (newData.length > oldData.length && this.isAtBottom()) {
          this.scrollToTheLastLogs();
        }
      },
      'uploadingDataStatus.isProgress': {
        handler() {
          clearInterval(this.intervalId);

          this.startLogsInterval(this.getUploadingDataStatus);

          this.fetchData();
        },
      },
      'uploadingDataStatus.processingId': {
        handler() {
          if (!this.getUploadingDataStatus.isProgress || this.type !== this.getUploadingDataStatus.type) return;

          this.initAbortController?.abort();

          this.lastProcessingUploadingDataStatus = { ...this.getUploadingDataStatus };
        },
        immediate: true,
      },
      isUploadingFile: {
        handler(value: boolean) {
          if (value) {
            this.logs = [];
          }
        },
      },
    },
  });
